<script>
import StoreService from '@/services/StoreService'
import Tabs from '@/components/MarketplaceComponents/Tabs.vue'
import Breadcrumbs from '@/components/MarketplaceComponents/Breadcrumbs.vue'
import PageTitle from '@/components/MarketplaceComponents/PageTitle.vue'
import Pagination from '@/components/Pagination/Pagination.vue'
import PageArticle from '@/components/MarketplaceComponents/PageArticle'
import ProductCard from '@/components/MarketplaceComponents/ProductCard'
import { mapActions, mapGetters, mapState } from 'vuex'
import { createMeta } from '~/utils/meta'

export default {
  components: {
    ProductCard,
    PageArticle,
    Tabs,
    Breadcrumbs,
    PageTitle,
    Pagination
  },

  async asyncData ({ $axios, route }) {
    const { data } = await StoreService.getMarketplaceAppByPath($axios, route.params.app)

    const application = data?.data

    const currentCategoryId = application?.categories?.find(c => c.seo_data?.path === (route.params.category || ''))?.id

    const productsData = await StoreService.getMarketplaceProductsWithUserID($axios, 1, 12, currentCategoryId)

    const products = productsData?.data?.data?.products
    const productsCount = +productsData?.data?.data?.count?.[0]?.count

    return {
      application,
      products,
      productsCount
    }
  },

  data () {
    return {
      application: null,
      products: null,
      productsCount: 0,
      page: 0,
      pageSize: 12
    }
  },

  computed: {
    ...mapState('user', ['user']),
    ...mapState('marketplace', ['currencies']),
    ...mapGetters('user', ['isAuthorized', 'isAdmin']),

    tabs () {
      return [
        {
          id: 'game',
          title: this.$t('servicesInGame')
        },
        {
          id: 'app',
          title: this.$t('applications')
        },
        {
          id: 'social',
          title: this.$t('socialNetworks')
        },
        {
          id: 'software',
          title: this.$t('software')
        },
        {
          id: 'offerwall',
          title: this.$t('offerwalls')
        },
        {
          id: 'others',
          title: this.$t('others')
        }
      ]
    },

    pageArticle () {
      if (this.seoData?.[this.locale]) {
        return this.seoData[this.locale]?.article
      }

      return undefined
    },

    activeTabId () {
      return this.application?.app_type || this.tabs[0].id
    },

    appPath () {
      return this.$route.params.app
    },

    currentCategory () {
      return this.$route.params.category || ''
    },

    currentCategoryId () {
      return this.categories?.find(c => c.seo_data?.path === this.currentCategory)?.id
    },

    currentCategoryName () {
      return this.categories?.find(c => c.seo_data?.path === this.currentCategory)?.category_name
    },

    seoData () {
      return this.categories?.find(c => c.seo_data?.path === this.currentCategory)?.seo_data
    },

    currentTabTitle () {
      return this.tabs?.find(tab => tab.id === this.activeTabId)?.title
    },

    appName () {
      return this.application?.title || ''
    },

    h1Title () {
      if (this.$i18n.locale === 'ru') {
        return `${this.$t(this.currentCategoryName)} ${this.appName}`
      }

      return `${this.appName} ${this.currentCategoryName}`
    },

    h1Text () {
      if (this.$i18n.locale === 'ru' && this.seoData?.ru?.h1) {
        return this.seoData?.ru?.h1
      } else {
        return this.h1Title
      }
    },

    breadcrumbs () {
      return [
        { title: this.$t('marketplace'), to: '/marketplace' },
        {
          title: this.appName,
          to: `/marketplace/${this.$route.params.app}`
        },
        {
          title: this.$t(this.currentCategoryName)
        }
      ]
    },

    goBackPath () {
      return `/marketplace/${this.$route.params.app}`
    },

    categories () {
      return this.application?.categories
    },

    isSeller () {
      return this.user?.is_seller
    },

    pagesCount () {
      if (!this.productsCount || this.productsCount === 0) {
        return 1
      }

      return Math.ceil(this.productsCount / this.pageSize)
    },

    showRub () {
      return this.$i18n.locale === 'ru'
    },

    rubCurrencyRate () {
      return this.currencies?.find(c => c.code === 'RUB')?.exchange_rate || 0
    },

    locale () {
      return this.$i18n.locale?.split?.('-')[0] || 'en'
    }
  },

  watch: {
    appPath () {
      this.loadApp()
    },

    currentCategory () {
      this.loadProducts()
    },

    pageSize () {
      this.page = 0
      this.loadProducts()
    },

    page () {
      this.loadProducts()
    }
  },

  methods: {
    ...mapActions('modal', ['showModal']),

    async loadApp () {
      const { data } = await StoreService.getMarketplaceAppByPath(this.$axios, this.appPath)

      this.application = data?.data
    },

    productDescription (product) {
      if (product.short_description && product.short_description !== '') {
        return product.short_description
      }

      return product.description
    },

    async loadProducts () {
      const { data } = await StoreService.getMarketplaceProductsWithUserID(this.$axios, this.page + 1, this.pageSize, this.currentCategoryId)

      this.products = data?.data?.products
      this.productsCount = +data?.data?.count?.[0]?.count
    },

    setActiveTab (tabId) {
      this.$router.push(this.localeLocation('/'))
    },

    badSeo (id) {
      const seo = this.categories?.find(c => c.id === id)?.seo_data?.[this.locale]
      return !(seo?.title?.length && seo?.productTitle?.length && seo?.h1?.length && seo?.article?.length && seo?.keywords?.length && seo?.description?.length)
    },

    categoryStyles ({ id }) {
      return {
        [this.$style.tag]: true,
        [this.$style.tagActive]: id === this.currentCategoryId,
        [this.$style.tagNoSeo]: this.isAdmin && this.badSeo(id)
      }
    },

    categoryName (category) {
      return category.translations?.[this.$i18n.locale.split('-')[0]]?.category_name || this.$t(category.category_name?.trim())
    },

    appWithCategoryLink (category) {
      return this.localeLocation(`/marketplace/${this.appPath}/${category.seo_data.path}`)
    },

    addNewService () {
      this.$router.push(this.localeLocation(`/marketplace/add?type=${this.activeTabId}&app=${this.application.id}&category=${this.currentCategoryId}`))
    },

    maxPriceInRub (product) {
      return Math.ceil(product?.max_price / (this.rubCurrencyRate * 0.98) / 100)
    },

    isFavourite (product) {
      return product?.favourite
    },

    openAuthModal () {
      this.showModal({
        component: 'Auth',
        data: {
          mode: 'register'
        }
      })
    },

    editSEO () {
      this.showModal({
        component: 'editSEOModal',
        data: {
          category: this.categories?.find(c => c.seo_data?.path === this.currentCategory),
          loadApp: this.loadApp
        }
      })
    },

    addToFavourites (product) {
      if (!this.isAuthorized) {
        this.openAuthModal()
      } else if (this.isFavourite(product)) {
        StoreService.dropFavourite(this.$axios, product?.id)
        product.favourite = false
      } else {
        StoreService.addToFavourites(this.$axios, product?.id)
        product.favourite = true
      }
    }
  },

  head () {
    const seo = this.seoData?.[this.locale]
    if (seo) {
      return createMeta.call(this, {
        title: seo.title || this.$t('applicationCategoryPageMetaTitle', { app: this.application?.title || '', category: this.$t(this.currentCategoryName) }),
        description: seo.description || this.$t('applicationCategoryPageMetaDescription', { app: this.application?.title || '', category: this.$t(this.currentCategoryName) }),
        sharingTitle: seo.title || this.$t('applicationCategoryPageMetaTitle', { app: this.application?.title || '', category: this.$t(this.currentCategoryName) }),
        sharingDescription: seo.description || this.$t('applicationCategoryPageMetaDescription', { app: this.application?.title || '', category: this.$t(this.currentCategoryName) }),
        keywords: seo.keywords || undefined
      })
    }

    return createMeta.call(this, {
      title: this.$t('applicationCategoryPageMetaTitle', { app: this.application?.title || '', category: this.$t(this.currentCategoryName) }),
      description: this.$t('applicationCategoryPageMetaDescription', { app: this.application?.title || '', category: this.$t(this.currentCategoryName) })
    })
  }
}
</script>

<template>
  <div :class="$style.page">
    <Tabs :tabs="tabs" :active-tab-id="activeTabId" @setActiveTab="setActiveTab" />

    <Breadcrumbs :list="breadcrumbs" />

    <div v-if="application" :class="$style.page__body">
      <PageTitle with-go-back :go-back-path="goBackPath" :title="h1Text || h1Title" />

      <Button v-if="isSeller" is-primary :class="$style.seller" @click="addNewService">
        {{ $t('createService') }}
      </Button>

      <Button v-if="isAdmin" is-secondary :class="$style.seller" @click="editSEO">
        {{ $t('editSEO') }}
      </Button>

      <div :class="$style.tags">
        <NuxtLink v-for="category in categories" :key="category.id" :class="categoryStyles(category)" :to="appWithCategoryLink(category)">
          {{ categoryName(category) }} ({{ category.count }})
        </NuxtLink>
      </div>

      <div v-if="products" :class="$style.products">
        <div v-for="product in products" :key="product.id">
          <ProductCard :product="product" short :with-category-text="false" with-seller-text />
        </div>
      </div>

      <Pagination
        :class="$style.pagination"
        :per-page="pageSize"
        :pages-count="pagesCount"
        :current-page="page"
        @perPageChange="(pp) => { pageSize = pp }"
        @pageChange="(p) => { page = p }"
      />
    </div>

    <div v-if="pageArticle" :class="$style.page__body">
      <PageArticle :content="pageArticle" />
    </div>
  </div>
</template>

<style lang="scss" module>
.page {
  width: 100%;

  &__body {
    padding: 16px;
  }
}

.seller {
  width: 100% !important;
  margin-bottom: 24px;
}

.tags {
  display: flex;
  flex-wrap: wrap;
}

.tag {
  padding: 8px 12px;
  background: $blue-grey50;
  color: $white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  margin-right: 8px;
  margin-bottom: 8px;
  border: 1px solid $blue-grey50;
  cursor: pointer;

  &:last-child {
    margin-right: 0;
  }

  &Active {
    background: rgba($primary60, .2);
    border: 1px solid $primary60;
  }

  &NoSeo {
    background: $red60-20 !important;
  }
}

.products {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 8px;
  margin-top: 24px;

  @media (min-width: $breakpointTablet) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: $breakpointDesktopWide) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
}

.product {
  display: block;
  height: 100%;
  background: $blue-grey70;
  color: $white;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;

  &__description {
    width: 100%;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    color: $white;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    padding-top: 12px;
    margin-top: 12px;
    border-top: 1px solid $blue-grey40;
  }

  &__dealer {
    display: grid;
    grid-template-columns: max-content 1fr;
    grid-gap: 8px;

    &Right {
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: 4px;

      &Top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 4px;
      }

      &Bottom {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    }

    &Name {
      color: $white;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
    }

    &Avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      object-fit: cover;
    }

    &Reviews {
      color: $blue-grey10;
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;
    }

    &Price {
      color: $white;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
    }
  }
}

.pagination {
  margin: 40px 0;
}

.likeContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  background: $blue-grey60;
  border-radius: 8px;
  height: 40px;
  width: 40px;
  margin-left: 12px;
  cursor: pointer;
}

.like {
  height: 24px;
  width: 24px;

  &Active {
    path {
      fill: $primary60;
    }
  }
}

.hiddenH1 {
  display: none;
}
</style>
